import type { InsertToPosition } from '@blocksuite/affine-shared/utils';
import { nanoid } from '@blocksuite/store';
import { computed, type ReadonlySignal, signal } from '@preact/signals-core';

import type { DataSource } from '../data-source/base.js';
import type {
  DataViewDataType,
  DataViewMode,
  ViewMeta,
} from '../view/data-view.js';
import type { SingleView } from './single-view.js';

type ViewModeSnapshots = NonNullable<DataViewDataType['viewModeSnapshots']>;

export function getViewModeSnapshots(
  data: DataViewDataType
): ViewModeSnapshots {
  return { ...(data.viewModeSnapshots ?? {}) };
}

export function snapshotViewModeData(
  data: DataViewDataType
): Record<string, unknown> {
  const { viewModeSnapshots: _snapshots, ...snapshot } = data;
  return { ...snapshot };
}

function deleteStaleViewModeKeys(
  oldData: DataViewDataType,
  nextData: DataViewDataType
): Partial<DataViewDataType> {
  const deletes: Record<string, undefined> = {};
  const nextKeys = new Set(Object.keys(nextData));
  for (const key of Object.keys(oldData)) {
    if (!nextKeys.has(key)) {
      deletes[key] = undefined;
    }
  }
  return deletes as Partial<DataViewDataType>;
}

export interface ViewManager {
  viewMetas: ViewMeta[];
  dataSource: DataSource;
  readonly$: ReadonlySignal<boolean>;

  currentViewId$: ReadonlySignal<string | undefined>;
  currentView$: ReadonlySignal<SingleView | undefined>;

  setCurrentView(id: string): void;

  views$: ReadonlySignal<string[]>;

  viewGet(id: string): SingleView | undefined;

  viewAdd(type: DataViewMode): string;

  viewAddFrom(
    sourceViewId: string,
    targetMode: DataViewMode,
    options?: {
      override?: (data: DataViewDataType) => DataViewDataType;
    }
  ): string;

  viewDelete(id: string): void;

  viewDuplicate(id: string): void;

  viewDataGet(id: string): DataViewDataType | undefined;

  moveTo(id: string, position: InsertToPosition): void;

  viewChangeType(id: string, type: string): void;
}

export class ViewManagerBase implements ViewManager {
  _currentViewId$ = signal<string | undefined>(undefined);

  views$ = computed(() => {
    return this.dataSource.viewDataList$.value.map(data => data.id);
  });

  currentViewId$ = computed(() => {
    const views = this.views$.value;
    const currentViewId = this._currentViewId$.value;
    return currentViewId && views.includes(currentViewId)
      ? currentViewId
      : views[0];
  });

  currentView$ = computed(() => {
    const id = this.currentViewId$.value;
    if (!id) return;
    return this.viewGet(id);
  });

  readonly$ = computed(() => {
    return this.dataSource.readonly$.value;
  });

  get viewMetas() {
    return this.dataSource.viewMetas;
  }

  constructor(public dataSource: DataSource) {}

  moveTo(id: string, position: InsertToPosition): void {
    this.dataSource.viewDataMoveTo(id, position);
  }

  setCurrentView(id: string | undefined): void {
    this._currentViewId$.value = id;
  }

  viewAdd(type: DataViewMode): string {
    const meta = this.dataSource.viewMetaGet(type);
    const data = meta.model.defaultData(this);
    const id = this.dataSource.viewDataAdd({
      ...data,
      id: nanoid(),
      name: meta.model.defaultName,
      mode: type,
    });
    this.setCurrentView(id);
    return id;
  }

  viewAddFrom(
    sourceViewId: string,
    targetMode: DataViewMode,
    options?: {
      override?: (data: DataViewDataType) => DataViewDataType;
    }
  ): string {
    if (this.readonly$.value) {
      throw new Error('Cannot create a view in a readonly database');
    }

    const source = this.dataSource.viewDataGet(sourceViewId);
    if (!source) {
      throw new Error(`Source view not found: ${sourceViewId}`);
    }
    const targetMeta = this.dataSource.viewMetaGet(targetMode);
    const snapshots = getViewModeSnapshots(source);
    snapshots[source.mode] = snapshotViewModeData(source);
    delete snapshots[targetMode];

    let next = {
      ...targetMeta.model.defaultData(this),
      id: nanoid(),
      name: source.name.trim(),
      mode: targetMode,
      viewModeSnapshots: snapshots,
    } as DataViewDataType;
    const convert = this.dataSource.viewConverts.find(
      candidate => candidate.from === source.mode && candidate.to === targetMode
    );
    if (convert) {
      next = {
        ...next,
        ...convert.convert(source),
        id: next.id,
        name: next.name,
        mode: targetMode,
        viewModeSnapshots: snapshots,
      } as DataViewDataType;
    }
    next = options?.override?.(next) ?? next;

    if (
      !next.id ||
      this.dataSource.viewDataGet(next.id) ||
      next.mode !== targetMode ||
      !next.name.trim() ||
      !('columns' in next) ||
      !Array.isArray(next.columns)
    ) {
      throw new Error('Converted view data is invalid');
    }
    if (
      this.readonly$.value ||
      this.dataSource.viewDataGet(sourceViewId) !== source
    ) {
      throw new Error('Source view changed before creation');
    }

    const id = this.dataSource.viewDataAdd(next);
    this.setCurrentView(id);
    return id;
  }

  viewChangeType(id: string, type: string): void {
    const meta = this.dataSource.viewMetaGet(type);
    this.dataSource.viewDataUpdate(id, old => {
      if (old.mode === type) {
        return {};
      }

      const snapshots = getViewModeSnapshots(old);
      snapshots[old.mode] = snapshotViewModeData(old);
      const restoredSnapshot = snapshots[type];
      let data = {
        ...(restoredSnapshot ?? meta.model.defaultData(this)),
        id: old.id,
        name: old.name,
        mode: type,
        viewModeSnapshots: snapshots,
      } as DataViewDataType;
      const convertFunction = this.dataSource.viewConverts.find(
        v => v.from === old.mode && v.to === type
      );
      if (!restoredSnapshot && convertFunction) {
        data = {
          ...data,
          ...convertFunction.convert(old),
          viewModeSnapshots: snapshots,
        };
      }
      return {
        ...deleteStaleViewModeKeys(old, data),
        ...data,
      };
    });
  }

  viewDataGet(id: string): DataViewDataType | undefined {
    return this.dataSource.viewDataGet(id);
  }

  viewDelete(id: string): void {
    const views = this.views$.value;
    if (views.length <= 1) {
      return;
    }
    const index = views.indexOf(id);
    const nextViewId =
      index >= 0
        ? (views[index + 1] ??
          views[index - 1] ??
          views.find(viewId => viewId !== id))
        : views[0];
    const shouldSelectFallback = this.currentViewId$.value === id;
    this.dataSource.viewDataDelete(id);
    if (shouldSelectFallback) {
      this.setCurrentView(nextViewId);
    }
  }

  viewDuplicate(id: string): void {
    const newId = this.dataSource.viewDataDuplicate(id);
    this.setCurrentView(newId);
  }

  viewGet(id: string): SingleView | undefined {
    const meta = this.dataSource.viewMetaGetById(id);
    if (!meta) return;
    return new meta.model.dataViewManager(this, id);
  }
}
